class SchedulePlayer {
  /**
   * @param {NekoPlayer} player
   * @param {string} archiveUrl アーカイブ配信用の URL
   */
  constructor(player, archiveUrl) {
    this._player = player
    this._archiveUrl = archiveUrl
    this._timerTag = new SchedulePlayer.TimerTag(player.container)
  }

  /**
   * @param {string} now 現在時刻（ISO 8601 形式）
   * @param {string} startAt 上映会配信開始時刻（ISO 8601 形式）
   * @param {string} endAt 上映会配信終了時刻（ISO 8601 形式）
   * @param {number} duration 映像の長さ
   */
  start(now, startAt, endAt, duration) {
    const formattedStartAt = new Date(startAt).toLocaleString(undefined, { timeZoneName: 'short' })
    this._timerTag.text = `${formattedStartAt} 配信開始`
    this._player.hideControls()

    const timerStateByFunctions = {
      [SchedulePlayer.TimerStatus.Opening]: (timer, elapsedTime) => {
        const remainingTime = elapsedTime * -1
        const minStr = String(Math.floor(remainingTime / 60)).padStart(2, '0')
        const secStr = String((remainingTime % 60)).padStart(2, '0')
        this._timerTag.text = `あと ${minStr}:${secStr} で配信がはじまります`
      },
      [SchedulePlayer.TimerStatus.Start]: (timer, elapsedTime) => {
        (async () => {
          await this._player.reloadData()
          await this._player.load(elapsedTime)
          if (this._player.assetUri) {
            this._player.muted = true
            this._player.start()
            this._timerTag.hidden = true
          } else {
            this._timerTag.text = `${formattedStartAt} より配信中`
          }
        })()
      },
      [SchedulePlayer.TimerStatus.Finished]: (timer, elapsedTime) => {
        timer.stop()

        this._timerTag.hidden = false
        this._timerTag.text = '配信は終了しました' + (this._archiveUrl ? '。アーカイブは' : '')
        this._timerTag.setArchiveLink(this._archiveUrl, 'こちら')

        this._player.showPosterImage()
        this._player.deleteAllEvents()
        this._player.hideControls()
        this._player.finishPlayback()
      }
    }

    const scheduleTimer = new SchedulePlayer.Timer(now, startAt, endAt, duration)
    scheduleTimer.start((timer, state, elapsedTime) => {
      if (timerStateByFunctions.hasOwnProperty(state)) {
        timerStateByFunctions[state](timer, elapsedTime)
      }
    })
  }
}

SchedulePlayer.Constants = {
  TIMER_INTERVAL: 1000, // 更新間隔（１秒毎に表示も含め更新）
  REMINDER_THRESHOLD: -300, // 5分前

  // 「動画を最後まで再生した」と判断されてから配信終了するまでの時間。単位は秒。
  //
  // 動画の長さと経過時間を見て「動画を最後まで再生したか」の判断をしているため、
  // 途中で一時停止をしなくても、実際に最後まで再生しきる直前に「最後まで再生した」と判断されてしまうことがある。
  // そこで、「最後まで再生した」と判断されてから配信終了するまでの時間に少し余裕をもたせる。
  MARGIN_TIME: 5
}

SchedulePlayer.TimerStatus = {
  Idling: Symbol('idling'),
  Opening: Symbol('opening'),
  Start: Symbol('start'),
  Playing: Symbol('playing'),
  Finished: Symbol('finished'),
  Unknown: Symbol('unknown')
}

SchedulePlayer.Timer = class {
  /**
   * @param {string} now 現在時刻（ISO 8601 形式）
   * @param {string} startAt 上映会配信開始時刻（ISO 8601 形式）
   * @param {string} endAt 上映会配信終了時刻（ISO 8601 形式）
   * @param {number} duration 映像の長さ
   */
  constructor(now, startAt, endAt, duration) {
    this._now = new Date(now)
    this._startAt = new Date(startAt)
    this._endAt = new Date(endAt)
    this._movieDuration = duration
    this._intervalTimer = null
    this._count = 0
    this._started = false
  }

  /**
   * @callback TimerCallback
   * @param {SchedulePlayer.Timer} timer
   * @param {SchedulePlayer.TimerStatus} state
   * @param {Number} elapsedTime
   */

  /**
   * @param {TimerCallback} callback
   */
  start(callback) {
    this.stop()
    this._intervalTimer = setInterval(
      () => this._countdown(callback),
      SchedulePlayer.Constants.TIMER_INTERVAL
    )
    // タイマー開始までのラグがあるため初回は即時呼ぶ
    this._countdown(callback)
  }

  stop() {
    this._stopCountdown()
  }

  /**
   * @param {TimerCallback} callback
   * @private
   */
  _countdown(callback) {
    const remainingTime = (this._startAt - this._now) / 1000
    const missedTime = -remainingTime
    const playDuration = (this._endAt - this._startAt) / 1000
    const elapsedTime = this._count - remainingTime

    let timerState = SchedulePlayer.TimerStatus.Unknown
    if (elapsedTime > playDuration || elapsedTime > this._movieDuration + SchedulePlayer.Constants.MARGIN_TIME) {
      // 上映会の配信終了時刻を過ぎた場合、または動画を最後まで再生し終わった場合

      timerState = SchedulePlayer.TimerStatus.Finished
    } else if (elapsedTime > 0) {
      // 既に配信が開始されている場合

      if (!this._started) {
        // 一度 TimerStatus.Start を返す
        this._started = true;
        callback(this, SchedulePlayer.TimerStatus.Start, missedTime)
      }
      timerState = SchedulePlayer.TimerStatus.Playing
    } else {
      if (elapsedTime >= 0) {
        // 配信開始時刻を経過した場合
        this._started = true
        timerState = SchedulePlayer.TimerStatus.Start
      } else if (elapsedTime >= SchedulePlayer.Constants.REMINDER_THRESHOLD) {
        // 配信開始 n 分前
        timerState = SchedulePlayer.TimerStatus.Opening
      } else {
        // 配信開始前
        timerState = SchedulePlayer.TimerStatus.Idling
      }
    }

    callback(this, timerState, elapsedTime)
    this._count++
  }

  /**
   * @private
   */
  _stopCountdown() {
    this._count = 0
    if (this._intervalTimer) {
      clearInterval(this._intervalTimer)
      this._intervalTimer = null
    }
  }
}

/**
 * <div id="schedule-timer-tag">
 *  <span>
 *    text <a href="archive url"> link text </a>
 *  </span>
 * </div>
 */
SchedulePlayer.TimerTag = class {

  /**
   * @param {HTMLElement} parentElement
   */
  constructor(parentElement) {
    const tagElement = document.createElement('div')
    tagElement.id = 'schedule-timer-tag'
    tagElement.classList.add('tag', 'is-medium', 'is-rounded', 'is-light')

    const textElement = document.createElement('span')
    const archiveLinkElement = document.createElement('a')

    tagElement.appendChild(textElement)
    tagElement.appendChild(archiveLinkElement)
    parentElement.appendChild(tagElement)

    this._tagElement = tagElement
    this._textElement = textElement
    this._archiveLinkElement = archiveLinkElement
  }

  get htmlElement() { return this._tagElement }

  /**
   * @param {string} value アーカイブ配信用 URL
   */
  set archiveLinkUrl(value) {
    this._archiveLinkElement.href = value
    this._archiveLinkElement.style.display = value ? 'inline' : 'none'
  }

  /**
   * @param {string} value アーカイブ配信用リンクテキスト
   */
  set archiveLinkText(value) {
    this._archiveLinkElement.textContent = value
  }

  /**
   * @param {string} value タイマータグ表示用のテキスト
   */
  set text(value) {
    this._textElement.textContent = value
  }

  /**
   * @param {Boolean} enable タイマータグの表示/非表示
   */
  set hidden(enable) {
    this._tagElement.style.display = enable ? 'none' : 'block'
  }

  /**
   * アーカイブ配信用の URL とリンクテキストを設定
   * @param {string} url
   * @param {string} linkText
   */
  setArchiveLink(url, linkText) {
    this.archiveLinkText = linkText
    this.archiveLinkUrl = url
  }
}

export default SchedulePlayer
